import { Link, useRouteLoaderData, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";
import DOMPurify from "dompurify";

import { getFileNameFromUrl } from "src/components/common/functions";

import { field, form } from "../types";

interface LoaderData {
  schema: {
    form: Pick<form, "id" | "name" | "type">;
    fields: field[];
  };
  values: Record<string, any>;
  record_id: number;
  record_name: string;
  pdfId: string;
}
export default function RecordDetail(){
    const {
        schema,
        values,
        record_id, 
        record_name,
        pdfId, 
    } = useRouteLoaderData("formRecord") as LoaderData;
    const { form, fields } = schema;
    const submit = useSubmit();
    function handleDelete(){
        const ok = window.confirm(
        "Are you sure you want to delete this record?\n\nThis action cannot be undone."
        );
        if (!ok) return;
        const fd = new FormData();
        fd.append("record_id", String(record_id));
        submit(fd, {
            method: "post",
            action: "Delete"
        });
    }
    const handleDownload = async () => {
        try{
            const response = await fetch(pdfId);
            if(!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(pdfId)!);
        }catch(error){
            window.alert("Error downloading file:" + error);
        }
    }
    return (
        <div className="cssContent pb8rem">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                <h4 className="mb-0">
                    {form.name}
                    <span className="text-muted ms-2 fw-normal">
                    ({form.type})
                    </span>
                </h4>
                <div className="text-muted small">
                    Record #{record_id} {record_name ? `- ${record_name}` : ""}
                </div>
                </div>
                <div>
                    {pdfId?
                    <button
                        children = "PDF"
                        type = "button"
                        className = "btn btn-primary me-3"
                        onClick = {handleDownload}
                    />:null}
                    <button
                        children = "Delete"
                        type = "button"
                        className = "btn btn-danger me-3"
                        onClick = {handleDelete}
                    />
                    <Link
                        to = "Edit"
                        className = "btn btn-outline-primary me-3"
                    >
                        Edit
                    </Link>
                    
                    <Link
                        to = ".."
                        className = "btn btn-outline-secondary"
                    >
                        Back
                    </Link>
                </div>
            </div>
            <div className = "card">
                <div className = "card-body">
                {fields.filter(field => field.field_type !== "signature").map(field => 
                field.field_type !== "display" ?
                <div
                    key = {field.field_key}
                    className = "mb-3"
                >
                    <div className = "fw-semibold">
                        {field.label}
                    </div>
                    <div className = "text-muted">
                        {renderValue(values[field.field_key], field.field_type, field.sub_form_id)}
                    </div>
                </div>
                :
                <div
                    key = {field.field_key}
                    className = "mb-3"
                    dangerouslySetInnerHTML = {{
                        __html: DOMPurify.sanitize(field.label ?? "", {
                            ALLOWED_TAGS: ["div", "b", "i", "strong", "em", "u", "br", "p", "ul", "ol", "li"],
                            ALLOWED_ATTR: ["class"]
                        })
                    }}
                />
                )}
                </div>
            </div>
        </div>
    );
}
function renderValue(value: any, type: string, sub_form_id: number | null){
    if(
        value === null ||
        value === undefined ||
        value === ""
    ){
        return <span className="text-muted">—</span>;
    }
    switch(type){
        case "textarea":
            return <span className = "cssNotes">{String(value)}</span>;
        case "user":
            return <div><Link children = {value.label} to = {`/Users/${value.value}`}/></div>;
        case "users":
            return value.map((v: any, vi: number) => <div key = {vi}><Link children = {v.label} to = {`/Users/${v.value}`}/></div>);
        case "sub_form":
            return value.map((v: any, vi: number) => <div key = {vi}><Link children = {v.label} to = {`/Forms/${sub_form_id}/${v.value}`}/></div>);
        case "picture":
            return <div className = "d-flex gap-2" children = {value.files.map((v: {id:string, url: string, caption: string}) => <figure key = {v.id}><img src = {v.url} width = {100} height = {100} className = "rounded border" alt = ""/><figcaption children = {v.caption}/></figure>)}/>;
        case "checkbox":
        case "multiSelect":
            return value.join(", ")
        default:
            return String(value);
    }
}